import { useState } from 'react';
import { useI18n } from '../i18n/context';
import { BUILDING_DEFS } from '../content/buildings';
import { UNIT_DEFS } from '../content/units';
import { ProductionKind, queueIndexOf } from '../sim/build';
import { PRODUCER_IDS, YARD_QUEUE } from '../content/production';

/**
 * שם המבנה המייצר לכל תור. **נגזר מהרישום ולא מרשימה שנייה** — תור חדש בתוכן
 * מקבל כאן שם אוטומטית, ואם המזהה לא נמצא זו שגיאת קומפילציה ולא שורה ריקה.
 */
const PRODUCER_NAME_KEYS: readonly TranslationKey[] = PRODUCER_IDS.map(
    (id) => BUILDING_DEFS.find((def) => def.id === id)?.nameKey ?? 'build_open',
);
import { cameoKey } from '../render/cameo';
import type { CameoMap } from '../render/cameo';
import type { PowerState } from '../sim/build';
import type { ProductionItem } from '../sim/types';
import type { TranslationKey } from '../i18n/he';

/*
 * סרגל הבנייה.
 *
 * זה המקום שבו נגמר המבנה של "שורת כפתורים בתחתית". סרגל שצריך להחזיק עשרות
 * פריטים בקטגוריות הוא פאנל צד עם טאבים — בדיוק כמו ב-RA2 ובצילומים שבר שלח.
 * הוא נפתח ונסגר, כדי שבטלפון הוא לא יאכל את שדה הקרב כשלא צריך אותו.
 *
 * הקטלוג נבנה מקובצי התוכן, לא מרשימה שנייה: **מבנה או יחידה חדשים מופיעים כאן
 * מעצמם.**
 *
 * **רשת אייקונים ולא רשימת כרטיסים** (בקשת בר, 10.08.2026). הקטלוג היה עמודה של
 * כרטיסים בגובה 78px עם תיאור מלא בכל אחד, כלומר 3-4 פריטים במסך של טלפון וגלילה
 * מתמדת. האייקונים מגיעים מ-`render/cameo.ts` — אותם מודלי קופסאות שרצים בשדה,
 * ולכן אפס נכסים ואפס פער בין הקטלוג למה שיוצא מהמפעל.
 *
 * **ומה שהתיאורים קנו לא נזרק — הוא עבר למקום אחד.** מלכודת 29 הייתה אמיתית: בר
 * שיחק ולא ידע מה כל תחנה עושה. אבל הפתרון "תיאור מלא בכל כרטיס תמיד" שילם עליו
 * בגלילה. עכשיו יש **מתג `i` יחיד**, וכל עוד הוא דלוק הקשה על פריט מסבירה במקום
 * להזמין. כפתור `i` לכל כרטיס נשקל ונפסל: ברשת של שלוש עמודות הוא יעד של 24px
 * בתוך יעד להזמנה, ואצבע שמפספסת אותו **מזמינה מבנה** — בדיוק מלכודת 27.
 */

export type BuildTab = 'buildings' | 'units';

/**
 * שם המבנה שדרוש כתנאי-קדם, לפי המזהה שלו בתוכן.
 * המזהה ב-`requires` הוא מזהה מבנה, ולכן הוא תמיד נפתר — אין כאן ניחוש.
 */
function requirementKey(id: string): TranslationKey {
    const def = BUILDING_DEFS.find((building) => building.id === id);
    return def?.nameKey ?? 'building_yard';
}

/**
 * תנאי-קדם שכל פריט בקטלוג דורש — המפקדה, למשל — אינו מידע. הוא רק מאריך כל
 * שורה. נגזר מהתוכן ולא מרשימה קשיחה, כך שהוא יישאר נכון גם אחרי שינוי בעץ.
 */
const IMPLIED_REQUIREMENTS = new Set(
    BUILDING_DEFS.filter((def) => def.buildable).reduce<readonly string[]>(
        (shared, def) => shared.filter((id) => (def.requires as readonly string[]).includes(id)),
        (BUILDING_DEFS.find((def) => def.buildable)?.requires ?? []) as readonly string[],
    ),
);

function visibleRequirements(requires: readonly string[]): readonly string[] {
    const shown = requires.filter((id) => !IMPLIED_REQUIREMENTS.has(id));
    // אם הכול משתמע — עדיין מראים את האחרון, כדי שלא תישאר שורה ריקה בלי סיבה.
    return shown.length > 0 ? shown : requires.slice(-1);
}

interface BuildPanelProps {
    readonly credits: number;
    readonly power: PowerState;
    /** תור לכל מבנה ייצור, לפי הסדר של `content/production.ts`. */
    readonly queues: readonly (readonly ProductionItem[])[];
    readonly canAfford: (kind: number, type: number) => boolean;
    readonly available: (kind: number, type: number) => boolean;
    readonly onQueue: (kind: number, type: number) => void;
    readonly onCancel: (queueIndex: number) => void;
    readonly placing: boolean;
    readonly onPlace: () => void;
    readonly rallying: boolean;
    readonly onRally: () => void;
    /** מציג את הסבר מד החשמל בשורת הסטטוס. במגע אין ריחוף. */
    readonly onExplainPower: () => void;
    /** האם אין מספיק אשראי לפריט שבראש כל תור. */
    readonly starved: boolean;
    /** אייקוני הקטלוג, לפי `cameoKey`. ריק = נפילה חזרה לשם ולמחיר בלבד. */
    readonly cameos: CameoMap;
}

export function BuildPanel({
    credits,
    power,
    queues,
    canAfford,
    available,
    onQueue,
    onCancel,
    placing,
    onPlace,
    rallying,
    onRally,
    onExplainPower,
    starved,
    cameos,
}: BuildPanelProps) {
    const { t, localeDef } = useI18n();
    const [tab, setTab] = useState<BuildTab>('buildings');
    const [open, setOpen] = useState(false);
    /*
     * מצב הסבר. כמו "הצב" ו"תקוף-נוע" — מתג מפורש ולא ריחוף, כי `title` אינו
     * קיים במגע (מלכודת 50) והמכשיר העיקרי הוא הטלפון.
     */
    const [explain, setExplain] = useState(false);
    /** מה שמוצג בשורת ההסבר. ריק = הטקסט המנחה. */
    const [detail, setDetail] = useState('');

    const isBuildings = tab === 'buildings';
    const kind = isBuildings ? ProductionKind.Building : ProductionKind.Unit;
    /*
     * הקטלוג נבנה מהתוכן, כולל התיאור והחשמל. **שם ומחיר בלבד מבקשים מהשחקן
     * לנחש מה הוא בונה** — בר שיחק ולא ידע מה כל תחנה עושה.
     */
    const items = isBuildings
        ? BUILDING_DEFS.map((def, type) => ({
              type,
              buildable: def.buildable,
              nameKey: def.nameKey,
              descKey: def.descKey,
              cost: def.cost,
              power: def.powerProduced - def.powerConsumed,
              requires: def.requires,
          })).filter((item) => item.buildable)
        : UNIT_DEFS.map((def, type) => ({
              buildable: true,
              type,
              nameKey: def.nameKey,
              descKey: def.descKey,
              cost: def.cost,
              power: 0,
              requires: [def.producedBy],
          }));
    /*
     * **שורת התור מציגה את תור המבנים בלבד.** מרגע שיש תור לכל מבנה ייצור אין
     * "התור" אחד להציג, וההתקדמות של כל יחידה כבר מצוירת על הכרטיס שלה. מה
     * שנשאר לשורה הזו הוא הדבר שאין לו מקום אחר: הכפתור "הצב".
     */
    const buildQueue = queues[YARD_QUEUE] ?? [];
    const head = buildQueue[0];

    /** הפריט שנבנה כרגע מסוג נתון, אם הוא בראש התור שלו. */
    const activeItem = (type: number): ProductionItem | undefined => {
        const queue = queues[queueIndexOf({ kind, type })];
        return queue?.[0]?.type === type ? queue[0] : undefined;
    };

    /** כמה עותקים מהפריט ממתינים בתור שלו. */
    const queuedCount = (type: number): number =>
        (queues[queueIndexOf({ kind, type })] ?? []).filter((item) => item.type === type).length;

    const lowPower = power.consumed > power.produced;

    return (
        /*
         * **`dir` מוצהר כאן במפורש.** הפאנל יושב בתוך `.game__canvasWrap` שמקובעת
         * `dir="ltr"` — נכון, כי מערכת הקואורדינטות לא יכולה להתהפך עם השפה —
         * ולכן הוא **ירש** LTR: התג נפתר לצד השם וכיסה אותו, התיאורים בעברית קיבלו
         * נקודה בתחילת השורה, והשם והמחיר התחלפו מקומות. מלכודות 2 ו-49.
         */
        <div className={open ? 'build build--open' : 'build'} dir={localeDef.dir}>
            <button className="build__toggle" type="button" onClick={() => setOpen((v) => !v)}>
                {open ? '×' : t('build_open')}
            </button>

            {/* הכסף והחשמל נראים תמיד, גם כשהפאנל סגור — הם המידע שמסתכלים עליו כל הזמן. */}
            <div className="build__meters">
                <span className="build__credits num">{credits.toLocaleString('en-US')}</span>
                {/*
                    **צריכה מול ייצור, ולא רק העודף.**

                    בר שאל "איך אני יודע כמה חשמל אני צריך?" — ולא היה איך: המספר
                    היחיד היה העודף, בלי תווית ובלי הקשר. "‏⚡70" לא אומר אם זה 70
                    מתוך 100 או 70 מתוך 500, ולכן אי אפשר לדעת אם תחנת כוח נוספת
                    היא השקעה או בזבוז. הפס מראה כמה מהייצור כבר תפוס.
                */}
                {/*
                    **הקשה, לא ריחוף.** ההסבר ישב ב-`title` בלבד, ו-`title` אינו
                    קיים במגע — מלכודת 50, בקוד שנכתב אחריה. וזה בדיוק הטקסט שעונה
                    על השאלה ששאל בר.
                */}
                <button
                    className={lowPower ? 'build__power build__power--low' : 'build__power'}
                    type="button"
                    title={t('build_power_hint')}
                    onClick={() => onExplainPower()}
                >
                    ⚡
                    <span className="build__powerBar">
                        <span
                            className="build__powerFill"
                            style={{
                                width: `${Math.min(
                                    100,
                                    Math.round(
                                        (power.consumed / Math.max(1, power.produced)) * 100,
                                    ),
                                )}%`,
                            }}
                        />
                    </span>
                    <span className="num">
                        {power.consumed}/{power.produced}
                    </span>
                </button>
            </div>

            {open && (
                <>
                    {/*
                        **שורת התור יושבת מעל הקטלוג, לא מתחתיו.**

                        זה היה הבאג שגרם לבר לומר "אני בוחר מבנה וכלום לא מתווסף":
                        הקטלוג הוא שישה כרטיסים בגובה 78px כל אחד, ובטלפון ב-375px
                        הפאנל נחתך לפני סופו. פס ההתקדמות וכפתור "הצב" נוצרו מתחת לקו
                        החיתוך, בלי שום חיווי שיש עוד תוכן — ולכן ההקשה נראתה כאילו
                        לא עשתה כלום, ולא הייתה שום דרך להגיע ל"הצב".

                        המשוב על מה שקורה עכשיו קודם לרשימת מה שאפשר לעשות.
                    */}
                    {/*
                        **"הצב" יושב כאן, מעל הכול, ולעולם לא זז.**

                        הוא היה **אחרי** שורות התור, ולכן ירד 52px בכל פעם שתור
                        יחידות התחיל וקפץ חזרה כשהוא התרוקן — כלומר הכפתור הכי
                        חשוב בזרימה היה זה שזז הכי הרבה. מלכודת 27, בפעם הרביעית.
                        מעומעם כשאין מה להציב, לא נעלם: זה בדיוק הכלל שכבר כתוב.
                    */}
                    <button
                        className={
                            placing ? 'build__place build__place--active' : 'build__place'
                        }
                        type="button"
                        disabled={!head?.ready}
                        onClick={() => {
                            /*
                             * **הפאנל נסגר ברגע שנכנסים למצב הצבה.** הוא תופס
                             * כמעט את כל רוחב המסך בטלפון, ולכן הבסיס — המקום
                             * היחיד שאפשר לבנות בו — היה מוסתר מתחתיו.
                             */
                            setOpen(false);
                            onPlace();
                        }}
                    >
                        {head?.ready
                            ? `${t('build_place')} · ${t(
                                  BUILDING_DEFS[head.type]?.nameKey ?? 'build_open',
                              )}`
                            : t('build_place')}
                    </button>

                    {/*
                        מקטע התורים בגובה **קבוע**. הוא היה גדל ומתכווץ עם מספר
                        התורים הפעילים, וכל שינוי כזה הזיז את כל הקטלוג ב-52px —
                        אצבע שנשארה במקום עברה 59% מהכרטיס לכרטיס שמעליו, כלומר
                        הקשה כפולה על תחנת כוח (300) הזמינה מפעל עיבוד (2000).
                    */}
                    <div className="build__queues">
                        {queues.map((queue, index) => {
                            const item = queue[0];
                            if (!item) return null;
                            /*
                             * **המחיר לפי סוג הפריט עצמו, לא לפי הטאב הפתוח.**
                             * קודם המחלק נלקח מ-`isBuildings`, ולכן אותה תחנת כוח
                             * הראתה 9% בטאב יחידות ו-53% בטאב מבנים — היחס בין
                             * מחירה למחיר הקוצר, שיושב באותו אינדקס בטבלה השנייה.
                             */
                            const def =
                                item.kind === ProductionKind.Building
                                    ? BUILDING_DEFS[item.type]
                                    : UNIT_DEFS[item.type];
                            const percent = Math.round(
                                (item.paid / Math.max(1, def?.cost ?? 1)) * 100,
                            );
                            return (
                                <div className="build__queue" key={index}>
                                    <div className="build__progress">
                                        <span
                                            className={
                                                starved && !item.ready
                                                    ? 'build__progressFill build__progressFill--starved'
                                                    : 'build__progressFill'
                                            }
                                            style={{ width: `${item.ready ? 100 : percent}%` }}
                                        />
                                    </div>
                                    <span className="build__queueName">
                                        {t(def?.nameKey ?? 'build_open')}
                                    </span>
                                    {/*
                                        שם המבנה המייצר, כאח ולא כילד: הוא הופך
                                        ארבע שורות אנונימיות לארבעה ערוצים, אבל
                                        אסור לו להיבלע בתוך שם הפריט.
                                    */}
                                    <span className="build__queueFrom">
                                        {t(PRODUCER_NAME_KEYS[index] ?? 'build_open')}
                                    </span>
                                    {queue.length > 1 && (
                                        <span className="build__queueCount num">
                                            {queue.length}
                                        </span>
                                    )}
                                    <button
                                        className="build__action"
                                        type="button"
                                        onClick={() => onCancel(index)}
                                    >
                                        {t('build_cancel')}
                                    </button>
                                </div>
                            );
                        })}
                        {/*
                            **"אין מספיק אשראי" היה מצב בלתי נראה לגמרי.** הפס פשוט
                            קפא, ו"בונה לאט כי אין חשמל" נראה זהה ל"עצר כי אין כסף".
                        */}
                        {starved && <span className="build__starved">{t('build_no_credits')}</span>}
                        {queues.every((queue) => queue.length === 0) && (
                            <span className="build__queuesEmpty">{t('build_idle')}</span>
                        )}
                    </div>

                    {/*
                        נקודת התכנסות גלובלית. **מבנה נבחר גובר עליה** — ראו
                        `tryRally` — ולכן זו ברירת המחדל ולא הדרך היחידה.
                    */}
                    <button
                        className={rallying ? 'build__rally build__rally--on' : 'build__rally'}
                        type="button"
                        aria-pressed={rallying}
                        onClick={() => {
                            // נסגר כמו "הצב": צריך לראות את המפה כדי להקיש עליה.
                            setOpen(false);
                            onRally();
                        }}
                    >
                        {t('build_rally')}
                    </button>

                    <div className="build__tabs">
                        <button
                            className={isBuildings ? 'build__tab build__tab--active' : 'build__tab'}
                            type="button"
                            onClick={() => setTab('buildings')}
                        >
                            {t('build_tab_buildings')}
                        </button>
                        <button
                            className={!isBuildings ? 'build__tab build__tab--active' : 'build__tab'}
                            type="button"
                            onClick={() => setTab('units')}
                        >
                            {t('build_tab_units')}
                        </button>
                        {/*
                            **מתג אחד, לא כפתור לכל כרטיס.** ראו ההערה בראש הקובץ:
                            ‏`i` בפינת כרטיס ברוחב 105px הוא יעד של 24px בתוך יעד
                            להזמנה, ופספוס שלו מזמין מבנה.
                        */}
                        <button
                            className={explain ? 'build__info build__info--on' : 'build__info'}
                            type="button"
                            aria-pressed={explain}
                            aria-label={t('build_explain_hint')}
                            onClick={() => {
                                setExplain((v) => !v);
                                setDetail('');
                            }}
                        >
                            i
                        </button>
                    </div>

                    {/*
                        שורת ההסבר, בגובה **קבוע**, ומעל הרשת ולא מתחתיה.

                        מלכודת 35: מה שחייב להיראות אינו יכול לשכון אחרי רשימה
                        באורך שרירותי. ומלכודת 27: גובה שמשתנה עם אורך הטקסט היה
                        מזיז את כל הרשת בכל הקשה, כלומר האצבע נוחתת על פריט אחר
                        מזה שכיוונה אליו.
                    */}
                    <p
                        className={
                            detail || explain
                                ? 'build__detail build__detail--on'
                                : 'build__detail'
                        }
                    >
                        {detail || (explain ? t('build_explain_hint') : '')}
                    </p>

                    <div className="build__items">
                        {items.map((item) => {
                            const usable = available(kind, item.type);
                            const affordable = canAfford(kind, item.type);
                            /*
                                **ההתקדמות מצוירת על הכרטיס של מה שנבנה.**
                                זה גם מה ש-RA2 עושה, וזו הבקשה של בר: פס נפרד
                                מחייב לזכור מה הזמנת, וכרטיס שמתמלא **הוא** התשובה.
                                הכרטיס גם סופר כמה עותקים ממנו יש בתור.
                            */
                            const inQueue = queuedCount(item.type);
                            const active = activeItem(item.type);
                            const building = active !== undefined;
                            const percent = active
                                ? Math.round((active.paid / Math.max(1, item.cost)) * 100)
                                : 0;
                            const cameo = cameos.get(
                                cameoKey(isBuildings ? 'building' : 'unit', item.type),
                            );
                            const needs = `${t('build_needs')}: ${visibleRequirements(item.requires)
                                .map((id) => t(requirementKey(id)))
                                .join(' · ')}`;
                            const powerNote =
                                item.power > 0
                                    ? ` · ${t('build_power_gain')} +${item.power}`
                                    : item.power < 0
                                      ? ` · ${t('build_power_use')} ${-item.power}`
                                      : '';
                            return (
                                <button
                                    key={item.type}
                                    className={[
                                        'build__item',
                                        building ? 'build__item--building' : '',
                                        usable ? '' : 'build__item--locked',
                                    ]
                                        .filter(Boolean)
                                        .join(' ')}
                                    type="button"
                                    /*
                                        **הפריט אינו `disabled` גם כשהוא נעול.**
                                        קודם הקשה עליו לא עשתה כלום, והסיבה ישבה
                                        בשורת מטא שכבר אין לה מקום ברשת. עכשיו
                                        ההקשה **מסבירה מה חסר** — בדיוק הדפוס של
                                        קבוצת שליטה ריקה (מלכודת 50): הקשה שלא
                                        עושה כלום היא הקשה שנראית שבורה.
                                    */
                                    onClick={() => {
                                        if (!usable) {
                                            setDetail(needs);
                                            return;
                                        }
                                        if (explain) {
                                            setDetail(
                                                `${t(item.nameKey)} · ${item.cost} — ${t(
                                                    item.descKey,
                                                )}${powerNote}`,
                                            );
                                            return;
                                        }
                                        onQueue(kind, item.type);
                                    }}
                                >
                                    {building && (
                                        <span
                                            className="build__itemFill"
                                            style={{ width: `${active?.ready ? 100 : percent}%` }}
                                            aria-hidden="true"
                                        />
                                    )}
                                    {/*
                                        **גם 1.** בר: "כשאני בוחר מוצר והוא בתור
                                        צריך להופיע לי מספר עליו". הסתרת ה-1
                                        חסכה רעש ויזואלי ועלתה במידע היחיד
                                        שהשחקן באמת רוצה — האם ההקשה נקלטה.
                                    */}
                                    {inQueue > 0 && (
                                        <span className="build__itemBadge num">{inQueue}</span>
                                    )}
                                    {/*
                                        האייקון הוא אותו מודל שרץ בשדה. `alt` ריק
                                        בכוונה — השם כתוב מתחתיו, וקורא מסך היה
                                        מקריא אותו פעמיים.
                                    */}
                                    {cameo && (
                                        <img className="build__itemIcon" src={cameo} alt="" />
                                    )}
                                    <span className="build__itemName">{t(item.nameKey)}</span>
                                    <span
                                        className={
                                            affordable
                                                ? 'build__itemCost num'
                                                : 'build__itemCost build__itemCost--short num'
                                        }
                                    >
                                        {item.cost}
                                    </span>
                                </button>
                            );
                        })}
                    </div>
                </>
            )}
        </div>
    );
}
